import React, { useCallback, useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import useProductName from "brand-customization/useProductName";
import { Col, Form, ButtonToolbar, Button, Alert, Input } from "components/bootstrap";
import { FormikFormGroup } from "components/common";
import { Headline } from "components/common/Section/SectionComponent";
import { validateField, formHasErrors } from "util/FormsUtils";
import { OidcBackendMetaContext } from "authentication/components/oidc/config/components/OidcBackendMetaProvider";
import SelectionTypeOptions from "authentication/components/directoryServices/SelectionTypeOptions";
import SelectedGroupsOverview from "authentication/components/directoryServices/SelectedGroupsOverview";
import OktaMatchingGroupsOverview from "authentication/components/oidc/config/components/OktaMatchingGroupsOverview";
import payloadFromFormValues from "authentication/components/oidc/config/helpers/payloadFromFormValues";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
export const formValidation = (synchronizeGroups, selectionType) => {
  let teamSelection;
  let oktaApiToken;
  if (synchronizeGroups) {
    if (selectionType !== "all") {
      teamSelection = {
        required: true
      };
    }
    oktaApiToken = {
      required: true
    };
  }
  const validation = {
    oktaApiToken,
    teamDefaultRoles: {},
    teamSelectionType: {},
    teamSelection
  };
  return validation;
};
const GroupSyncForm = ({
  values,
  setFieldValue,
  isSubmitting,
  handleSubmit,
  validateForm,
  hasApiToken,
  submitAllError,
  goToPrevious
}) => {
  const { t } = useTranslation("enterprise_authentication");
  const productName = useProductName();
  const _formValidation = useCallback(
    (synchronizeGroups, teamSelectionType) => formValidation(synchronizeGroups, teamSelectionType),
    []
  );
  const validation = _formValidation(values.synchronizeGroups, values.teamSelectionType);
  const sendTelemetry = useSendTelemetry("oidc-groupsync-step");
  const _onSubmitAll = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.OIDC_GROUP_SYNC_SAVE_CLICKED, {
      app_action_value: "save-service"
    });
    if (values.synchronizeGroups) {
      return validateForm().then((errors) => {
        if (!formHasErrors(errors)) {
          handleSubmit();
        }
      });
    }
    return handleSubmit();
  };
  const _onGroupSelect = (groupId) => {
    const selectedGroupsIds = values.teamSelection;
    if (selectedGroupsIds == null ? void 0 : selectedGroupsIds.includes(groupId)) {
      setFieldValue("teamSelection", selectedGroupsIds.remove(groupId));
    } else {
      setFieldValue("teamSelection", selectedGroupsIds.add(groupId));
    }
  };
  const { licenseIsValid } = useContext(OidcBackendMetaContext);
  const disableGroupSyncStep = !licenseIsValid;
  const disableForm = disableGroupSyncStep || !values.synchronizeGroups;
  return /* @__PURE__ */ React.createElement(Col, { md: 7 }, /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info", title: t("oidc.group_sync_form.introduction_title") }, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "oidc.group_sync_form.introduction",
      values: { productName },
      components: { i: /* @__PURE__ */ React.createElement("i", null) }
    }
  )), /* @__PURE__ */ React.createElement(
    Input,
    {
      label: t("oidc.group_sync_form.enable_group_sync"),
      id: "synchronize-groups-checkbox",
      disabled: disableGroupSyncStep,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        label: t("oidc.group_sync_form.synchronize_groups"),
        disabled: disableGroupSyncStep,
        wrapperClassName: "col-sm-12",
        name: "synchronizeGroups",
        help: t("oidc.group_sync_form.synchronize_groups_help"),
        type: "checkbox"
      }
    )
  ), hasApiToken && values.oktaApiToken === void 0 ? /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "oktaApiToken",
      label: t("oidc.group_sync_form.okta_api_token"),
      help: t("oidc.group_sync_form.okta_api_token_help"),
      labelClassName: "col-sm-3",
      disabled: disableForm,
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("oktaApiToken", "") }, t("oidc.group_sync_form.reset_token"))
  ) : /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      buttonAfter: hasApiToken && values.oktaApiToken !== void 0 ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("oktaApiToken", void 0) }, t("oidc.group_sync_form.undo_reset")) : void 0,
      label: t("oidc.group_sync_form.okta_api_token"),
      disabled: disableForm,
      required: true,
      name: "oktaApiToken",
      placeholder: t("oidc.group_sync_form.okta_api_token"),
      validate: validateField(validation.oktaApiToken),
      help: t("oidc.group_sync_form.okta_api_token_help_existing"),
      type: "password"
    }
  ), /* @__PURE__ */ React.createElement(Headline, null, t("oidc.group_sync_form.load_matching_groups")), /* @__PURE__ */ React.createElement(
    OktaMatchingGroupsOverview,
    {
      disabled: disableForm,
      prepareSubmitPayload: payloadFromFormValues,
      onGroupSelect: _onGroupSelect
    }
  ), /* @__PURE__ */ React.createElement(Headline, null, t("oidc.group_sync_form.select_groups_to_import")), /* @__PURE__ */ React.createElement(SelectionTypeOptions, { disabled: disableForm }), /* @__PURE__ */ React.createElement(SelectedGroupsOverview, { onGroupSelect: _onGroupSelect, validation })), submitAllError, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.OIDC_PREVIOUS_SERVER_CONFIG_CLICKED, {
          app_action_value: "server-configuration-button"
        });
        goToPrevious();
      },
      type: "button"
    },
    t("oidc.group_sync_form.previous_server_configuration")
  ), /* @__PURE__ */ React.createElement(Button, { disabled: isSubmitting, onClick: _onSubmitAll }, t("oidc.group_sync_form.finish_and_save")))));
};
export default GroupSyncForm;
